import { useCallback, type FC, type PointerEvent } from 'react'
import styles from './resize-handle.module.css'

interface ResizeHandleProps {
  /** Current sidebar width as a percentage of the row (0-100). */
  sidebarWidth: number
  /** Persist the new width — typically writes to a Zustand store with localStorage. */
  setSidebarWidth: (width: number) => void
  /** Bounds the user can resize within. Default 15..70. */
  min?: number
  max?: number
}

/**
 * Thin draggable bar between the content pane and the sidebar in performance mode.
 * Updates `sidebarWidth` (a percentage of the parent row's width) on pointer drag.
 *
 * Why this and not a third-party splitter: keeping `content` under a stable parent
 * across sidebar toggles requires that the splitter component never unmount. Most
 * splitter libraries lose their internal sizing state when `right={null}`. A small
 * custom handle lets us stay in a stable React tree and still resize.
 */
export const ResizeHandle: FC<ResizeHandleProps> = ({ sidebarWidth, setSidebarWidth, min = 15, max = 70 }) => {
  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      const handle = event.currentTarget
      const row = handle.parentElement
      if (!row) {
        return
      }
      handle.setPointerCapture(event.pointerId)
      const rowRect = row.getBoundingClientRect()
      const startX = event.clientX
      const startWidth = sidebarWidth

      const handleMove = (moveEvent: globalThis.PointerEvent) => {
        const dxPct = ((moveEvent.clientX - startX) / rowRect.width) * 100
        // Dragging right increases content (left) width and decreases sidebar (right) width.
        const next = Math.max(min, Math.min(max, startWidth - dxPct))
        setSidebarWidth(next)
      }
      const handleUp = (upEvent: globalThis.PointerEvent) => {
        handle.releasePointerCapture(upEvent.pointerId)
        handle.removeEventListener('pointermove', handleMove)
        handle.removeEventListener('pointerup', handleUp)
        handle.removeEventListener('pointercancel', handleUp)
      }

      handle.addEventListener('pointermove', handleMove)
      handle.addEventListener('pointerup', handleUp)
      handle.addEventListener('pointercancel', handleUp)
    },
    [max, min, setSidebarWidth, sidebarWidth]
  )

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize notes panel"
      tabIndex={0}
      className={styles.handle}
      onPointerDown={handlePointerDown}
    />
  )
}

export default ResizeHandle
